import { useState, useEffect, useCallback } from "react";
import BackMethods from "../../back_component/BackMethods";
import { useNavigate, useLocation } from "react-router-dom";
import { FaArrowRight, FaShieldAlt } from "react-icons/fa";
import "../../styles/admin-shared.css";
import "../Users/users-admin.css";
import "./permissions-admin.css";
import PermissionsManagementModals from "./PermissionsManagementModals";

export default function PermissionsManagementPage() {
    const navigate = useNavigate();
    const location = useLocation();
    const { backEnd_request } = BackMethods();

    const [permissions, setPermissions] = useState([]);
    const [filteredPermissions, setFilteredPermissions] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [filterType, setFilterType] = useState("all");
    const [isLoading, setIsLoading] = useState(true);
    const [flash, setFlash] = useState(null);

    const [modal, setModal] = useState(null);
    const [modalPermissionId, setModalPermissionId] = useState(null);

    const fetchPermissions = useCallback(async () => {
        setIsLoading(true);
        try {
            const res = await backEnd_request.get("/admin/Permissions");
            const data = res.data.permissions || res.data || [];
            setPermissions(data);
            setFilteredPermissions(data);
        } catch (error) {
            console.error(error);
            setPermissions([]);
            setFilteredPermissions([]);
        } finally {
            setIsLoading(false);
        }
    }, [backEnd_request]);

    useEffect(() => {
        fetchPermissions();
    }, [fetchPermissions]);

    useEffect(() => {
        if (location.state?.successMessage) {
            setFlash({ message: location.state.successMessage, variant: "success" });
            navigate(".", { replace: true, state: {} });
        }
    }, [location.state, navigate]);

    useEffect(() => {
        if (!flash?.message) return undefined;
        const t = setTimeout(() => setFlash(null), 4500);
        return () => clearTimeout(t);
    }, [flash]);

    const closeModal = () => {
        setModal(null);
        setModalPermissionId(null);
    };

    const onModalSaved = ({ message, variant } = {}) => {
        fetchPermissions();
        setFlash({
            message: message || "تم حفظ التغييرات بنجاح",
            variant: variant || "success",
        });
    };

    const openCreate = () => setModal("create");

    const openEdit = (permissionId) => {
        setModalPermissionId(permissionId);
        setModal("edit");
    };

    const openDetails = (permissionId) => {
        setModalPermissionId(permissionId);
        setModal("details");
    };

    const handleSearch = (term, type) => {
        setSearchTerm(term);
        setFilterType(type);
        let filtered = permissions.filter((perm) => {
            const val = term.toLowerCase();
            if (type === "name") return perm.name?.toLowerCase().includes(val);
            if (type === "displayName") return perm.displayName?.toLowerCase().includes(val);
            if (type === "category") return perm.category?.toLowerCase().includes(val);
            return (
                perm.name?.toLowerCase().includes(val) ||
                perm.displayName?.toLowerCase().includes(val) ||
                perm.category?.toLowerCase().includes(val)
            );
        });
        setFilteredPermissions(filtered);
    };

    const handleDelete = async (permissionId) => {
        if (!window.confirm("هل أنت متأكد من حذف هذه الصلاحية؟")) return;
        try {
            await backEnd_request.delete(`/admin/Permissions/${permissionId}`);
            await fetchPermissions();
            setFlash({ message: "تم حذف الصلاحية بنجاح.", variant: "success" });
        } catch (error) {
            console.error(error);
            setFlash({
                message: error.response?.data?.message || "تعذر حذف الصلاحية. حاول مرة أخرى.",
                variant: "danger",
            });
        }
    };

    return (
        <div className="users-page-minimal users-admin-page" style={{ direction: "rtl", fontFamily: "Cairo, sans-serif" }}>
            <div className="container pb-5 pt-4 px-3">
                <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-3 pb-3 border-bottom gap-3">
                    <div>
                        <h3 className="fw-bold text-primary mb-1 d-flex align-items-center gap-2">
                            <span className="rounded-3 bg-primary bg-opacity-10 p-2 d-inline-flex text-primary">
                                <FaShieldAlt />
                            </span>
                            إدارة الصلاحيات
                        </h3>
                        <p className="text-muted small mb-0">التحكم في وصول المستخدمين لوظائف النظام الأساسية</p>
                    </div>
                    <div className="d-flex flex-wrap gap-2">
                        <button
                            type="button"
                            className="btn btn-primary d-flex align-items-center px-4 rounded-pill shadow-sm"
                            onClick={openCreate}
                        >
                            <i className="bi bi-shield-plus ms-2" /> إضافة صلاحية
                        </button>
                        <button type="button" onClick={() => navigate("/admin")} className="btn btn-outline-secondary rounded-pill">
                            <FaArrowRight className="ms-1" /> رجوع
                        </button>
                    </div>
                </div>

                {flash?.message ? (
                    <div
                        className={`users-admin-alert px-3 py-2 mb-3 d-flex align-items-center justify-content-between gap-2 ${flash.variant === "danger" ? "users-admin-alert--danger" : ""}`}
                        role="alert"
                    >
                        <span>{flash.message}</span>
                        <button type="button" className={`btn btn-sm btn-link p-0 ${flash.variant === "danger" ? "text-danger" : "text-success"}`} onClick={() => setFlash(null)}>
                            إغلاق
                        </button>
                    </div>
                ) : null}

                <div className="users-toolbar mb-3">
                    <div className="users-count-chip">
                        إجمالي الصلاحيات
                        <span>{permissions.length}</span>
                    </div>
                    <div className="users-search-wrap">
                        <div className="bg-white border rounded-4 p-2 shadow-sm">
                            <div className="row g-2 align-items-center">
                                <div className="col-md-9 border-end-md">
                                    <div className="input-group">
                                        <span className="input-group-text bg-transparent border-0 text-muted"><i className="bi bi-search" /></span>
                                        <input
                                            type="text"
                                            className="form-control border-0 shadow-none py-2"
                                            placeholder="ابحث عن اسم الصلاحية أو الفئة..."
                                            value={searchTerm}
                                            onChange={(e) => handleSearch(e.target.value, filterType)}
                                        />
                                    </div>
                                </div>
                                <div className="col-md-3">
                                    <select
                                        className="form-select form-select-sm border-0 shadow-none fw-medium"
                                        value={filterType}
                                        onChange={(e) => handleSearch(searchTerm, e.target.value)}
                                    >
                                        <option value="all">بحث شامل</option>
                                        <option value="name">الاسم التقني</option>
                                        <option value="displayName">الاسم الظاهر</option>
                                        <option value="category">الفئة</option>
                                    </select>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="users-table-shell">
                    {isLoading ? (
                        <div className="text-center py-5">
                            <div className="spinner-border spinner-border-sm text-secondary" />
                        </div>
                    ) : (
                        <div className="table-responsive">
                            <table className="table users-table align-middle mb-0 users-table--permissions">
                                <thead>
                                    <tr>
                                        <th className="text-center col-index">#</th>
                                        <th className="text-end users-role-display-head">الاسم الظاهر</th>
                                        <th className="text-end users-role-name-head">الاسم (تقني)</th>
                                        <th className="text-center">الفئة</th>
                                        <th className="text-center">النوع</th>
                                        <th className="text-center">الإجراءات</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredPermissions.length === 0 ? (
                                        <tr>
                                            <td colSpan={6}>
                                                <div className="empty-state">
                                                    <i className="bi bi-inbox fs-4 mb-2" />
                                                    <div>لا توجد صلاحيات مطابقة للبحث</div>
                                                </div>
                                            </td>
                                        </tr>
                                    ) : (
                                        filteredPermissions.map((perm, index) => {
                                            const descHint =
                                                (perm.description && String(perm.description).trim()) ||
                                                "لا يوجد وصف — انقر نقراً مزدوجاً لعرض التفاصيل";
                                            return (
                                                <tr
                                                    key={perm.permissionId}
                                                    title={descHint}
                                                    onDoubleClick={() => openDetails(perm.permissionId)}
                                                >
                                                    <td className="text-center col-index fw-semibold text-muted">{index + 1}</td>
                                                    <td className="text-end users-role-display-cell">
                                                        <span className="user-name">{perm.displayName || perm.name || "-"}</span>
                                                    </td>
                                                    <td className="text-end users-role-name-cell">
                                                        <span className="user-name text-muted small">{perm.name || "-"}</span>
                                                    </td>
                                                    <td className="text-center">
                                                        <span className="role-pill">{perm.category || "عام"}</span>
                                                    </td>
                                                    <td className="text-center">
                                                        <span
                                                            className={`permissions-type-pill ${perm.isSystemPermission ? "permissions-type-pill--system" : "permissions-type-pill--normal"}`}
                                                        >
                                                            {perm.isSystemPermission ? "نظام" : "عادي"}
                                                        </span>
                                                    </td>
                                                    <td className="text-center">
                                                        <div className="d-flex gap-2 justify-content-center flex-wrap">
                                                            <button
                                                                type="button"
                                                                className="btn-minimal"
                                                                title="تعديل"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    openEdit(perm.permissionId);
                                                                }}
                                                            >
                                                                <i className="bi bi-pencil" />
                                                            </button>
                                                            {!perm.isSystemPermission ? (
                                                                <button
                                                                    type="button"
                                                                    className="btn-minimal text-danger-hover"
                                                                    title="حذف"
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        handleDelete(perm.permissionId);
                                                                    }}
                                                                >
                                                                    <i className="bi bi-trash3" />
                                                                </button>
                                                            ) : null}
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })
                                    )}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>

                <PermissionsManagementModals
                    modal={modal}
                    permissionId={modalPermissionId}
                    onClose={closeModal}
                    onSaved={onModalSaved}
                />
            </div>
        </div>
    );
}